import { FormDrawer, FormDrawerContent, FormDrawerContentProps } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RegistrationFormSectionResourceTypeCompanionSection } from "../../../shared/types";
import { BlockRenderer } from "../../BlockRenderer";
import {
	CurrentCompanion,
	useHiddenDataFieldIdsInSection,
	useRegistrationForm,
} from "../../provider/registrationFormProviderContext";
import { RegistrationDataField } from "../../RegistrationDataField";
import { toGuestAttributeChildrenFromSection } from "../../registrationFormUtils";
import { useRegistrationCompanionForm } from "./useRegistrationCompanionForm";

interface Props {
	open: boolean;
	onClose: () => void;
	onClosed: () => void;
	stepId: number;
	currentCompanion: CurrentCompanion;
	section: RegistrationFormSectionResourceTypeCompanionSection;
	currentStepValues: Record<number, any>;
	companionTypeId: number;
	setError: (error: Record<number, string> | null) => void;
}

interface CompanionDrawerContentProps extends FormDrawerContentProps {
	stepId: number;
	currentCompanion: CurrentCompanion;
	section: RegistrationFormSectionResourceTypeCompanionSection;
	currentStepValues: Record<number, any>;
}

const RegistrationFormCompanionDrawerContent: FC<CompanionDrawerContentProps> = ({
	stepId,
	currentCompanion,
	section,
	currentStepValues,
	...contentProps
}) => {
	const { t } = useTranslation();
	const { validationRuleMap } = useRegistrationCompanionForm(section, currentCompanion);
	const { values: companionFormValues, setFieldValue } = useFormikContext<Record<number | string, unknown>>();
	const companionValuesAsStepValues = useMemo(() => {
		const companionValues = Object.fromEntries(
			Object.entries(companionFormValues).map(([key, value]) => [key.replace("companion_", ""), value]),
		);

		return {
			...currentStepValues,
			...companionValues,
		};
	}, [companionFormValues, currentStepValues]);
	const hiddenCompanionDataFieldIds = useHiddenDataFieldIdsInSection(companionValuesAsStepValues, stepId, section);

	useEffect(() => {
		for (const { dataField } of toGuestAttributeChildrenFromSection(section)) {
			if (hiddenCompanionDataFieldIds.includes(dataField.id)) {
				if (companionFormValues[`companion_${dataField.id}`] != null) {
					void setFieldValue(`companion_${dataField.id}`, undefined);
				}
			} else if (companionFormValues[`companion_${dataField.id}`] === undefined) {
				void setFieldValue(`companion_${dataField.id}`, null);
			}
		}
	}, [hiddenCompanionDataFieldIds, setFieldValue, companionFormValues, section]);

	return (
		<FormDrawerContent
			{...contentProps}
			submitButtonTitle={
				currentCompanion.index === -1
					? t("Add {{companion}}", { companion: section.companionSettings.companionType.singularLabel })
					: t("Update {{companion}}", { companion: section.companionSettings.companionType.singularLabel })
			}
		>
			{section.children.map((dataFieldOrBlock) => {
				if (dataFieldOrBlock.entityType === "block") {
					return (
						<BlockRenderer
							key={`${dataFieldOrBlock.entityType}_${dataFieldOrBlock.block.id}`}
							block={dataFieldOrBlock.block}
							sectionId={section.id}
						/>
					);
				} else if (!hiddenCompanionDataFieldIds.includes(dataFieldOrBlock.guestAttribute.dataField.id)) {
					return (
						<RegistrationDataField
							name={`companion_${dataFieldOrBlock.guestAttribute.dataField.id}`}
							key={`${dataFieldOrBlock.entityType}_${dataFieldOrBlock.guestAttribute.dataField.id}`}
							stepId={stepId}
							sectionId={section.id}
							dataField={dataFieldOrBlock.guestAttribute.dataField}
							validationRules={validationRuleMap[dataFieldOrBlock.guestAttribute.dataField.id] ?? []}
						/>
					);
				}
			})}
		</FormDrawerContent>
	);
};

export const RegistrationFormCompanionDrawer: FC<Props> = ({
	open,
	onClose,
	onClosed,
	stepId,
	section,
	currentCompanion,
	currentStepValues,
	companionTypeId,
	setError,
}) => {
	const { companionFormValidation } = useRegistrationForm();
	const { submitCompanion, initialValues } = useRegistrationCompanionForm(section, currentCompanion);

	return (
		<FormDrawer
			validate={(values) => companionFormValidation(stepId, currentStepValues, section, values)}
			open={open}
			onSubmit={async (values) => {
				submitCompanion(values, companionTypeId, setError, section, onClose);
				return true;
			}}
			initialValues={initialValues}
			onClose={onClose}
			onClosed={onClosed}
			title={section.companionSettings.companionType.singularLabel}
			renderForm={() => (
				<RegistrationFormCompanionDrawerContent
					title={section.companionSettings.companionType.singularLabel}
					onClose={onClose}
					currentCompanion={currentCompanion}
					stepId={stepId}
					section={section}
					currentStepValues={currentStepValues}
				/>
			)}
		/>
	);
};
